/**
 * chapterLoader.ts — Lazy chapter loading and node index.
 *
 * Uses Vite's import.meta.glob for code-split, on-demand loading
 * of chapter and sidequest JSON files. Builds a NodeIndex for
 * O(1) lookups by node ID.
 */

import type {
  ChapterData,
  SidequestData,
  StoryNode,
  NodeIndex,
  NodeIndexEntry,
  ClassId,
} from './types';

// ─── Raw JSON types (before normalization) ─────────────────────

/** Sidequest nodes may use content.default instead of description. */
interface RawSidequestNode {
  id: string;
  title: string;
  description?: string;
  content?: {
    default: string;
    class_specific?: Record<string, string>;
  };
  classSpecificText?: Record<string, string>;
  location: string;
  act: number;
  chapter: number;
  isCheckpoint?: boolean;
  contextTags?: string[];
  choices: unknown[];
  effects?: unknown[];
}

// ─── Glob imports (Vite code-splits each file) ─────────────────

const chapterModules = import.meta.glob<ChapterData>(
  './chapters/*.json',
  { import: 'default' }
);

const sidequestModules = import.meta.glob<SidequestData>(
  './sidequests/*.json',
  { import: 'default' }
);

// ─── Chapter number → file path mapping ────────────────────────

/** Map chapter numbers to their glob keys for fast lookup. */
function buildChapterPathMap(): Map<number, string> {
  const map = new Map<number, string>();
  for (const path of Object.keys(chapterModules)) {
    const match = path.match(/chapter(\d+)\.json$/);
    if (match) {
      map.set(parseInt(match[1], 10), path);
    }
  }
  return map;
}

/** Map class IDs to their sidequest glob keys. */
function buildSidequestPathMap(): Map<string, string> {
  const map = new Map<string, string>();
  for (const path of Object.keys(sidequestModules)) {
    // e.g. ./sidequests/frostblade-fighter-quest.json → frostblade-fighter
    const match = path.match(/\/([^/]+)-quest\.json$/);
    if (match) {
      // Convert filename to class ID: "frostblade-fighter" → "frostblade_fighter"
      map.set(match[1].replace(/-/g, '_'), path);
    }
  }
  return map;
}

const chapterPaths = buildChapterPathMap();
const sidequestPaths = buildSidequestPathMap();

// ─── Chapter normalization ─────────────────────────────────────

/**
 * Normalize a raw chapter into the standard ChapterData format.
 * Source JSON files use 3 different structures:
 *
 * Format A (ch 1-2): { chapter: { act, number, name, description }, nodes }
 * Format B (ch 3-7): { act, chapter: <number>, title, nodes }
 * Format C (ch 8-13): { chapterId/id, title, act, chapterNumber, description, nodes }
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normalizeChapter(raw: any, fileChapterNumber: number): ChapterData {
  // Format A: chapter is a metadata object
  if (raw.chapter && typeof raw.chapter === 'object') {
    return raw as ChapterData;
  }

  // Format B: chapter is an integer (the chapter number)
  if (typeof raw.chapter === 'number') {
    return {
      chapter: {
        act: raw.act,
        number: raw.chapter,
        name: raw.title ?? '',
        description: raw.description ?? '',
      },
      nodes: raw.nodes,
    };
  }

  // Format C: flat structure with chapterNumber
  return {
    chapter: {
      act: raw.act,
      number: raw.chapterNumber ?? fileChapterNumber,
      name: (raw.title ?? '').replace(/^Chapter \d+:\s*/, ''),
      description: raw.description ?? '',
    },
    nodes: raw.nodes,
  };
}

// ─── Node normalization ────────────────────────────────────────

/**
 * Normalize a raw sidequest node into a standard StoryNode.
 * Handles the mixed format where some nodes use content.default
 * instead of description, and content.class_specific instead
 * of classSpecificText.
 */
function normalizeNode(raw: RawSidequestNode): StoryNode {
  const description = raw.description ?? raw.content?.default ?? '';
  const classSpecificText =
    raw.classSpecificText ??
    (raw.content?.class_specific && Object.keys(raw.content.class_specific).length > 0
      ? raw.content.class_specific
      : undefined);

  return {
    id: raw.id,
    title: raw.title,
    description,
    classSpecificText: classSpecificText as StoryNode['classSpecificText'],
    location: raw.location,
    act: raw.act,
    chapter: raw.chapter,
    isCheckpoint: raw.isCheckpoint,
    contextTags: raw.contextTags ?? [],
    choices: raw.choices as StoryNode['choices'],
    effects: raw.effects as StoryNode['effects'],
  };
}

// ─── Public API ────────────────────────────────────────────────

/** Cache of loaded chapters by chapter number. */
const chapterCache = new Map<number, ChapterData>();

/** Cache of loaded sidequests by class ID. */
const sidequestCache = new Map<string, SidequestData>();

/**
 * Load a chapter by number. Returns cached data on subsequent calls.
 * @param chapterNumber 1–13
 */
export async function loadChapter(chapterNumber: number): Promise<ChapterData> {
  const cached = chapterCache.get(chapterNumber);
  if (cached) return cached;

  const path = chapterPaths.get(chapterNumber);
  if (!path) {
    throw new Error(`Chapter ${chapterNumber} not found`);
  }

  const loader = chapterModules[path];
  const raw = await loader();
  const data = normalizeChapter(raw, chapterNumber);
  chapterCache.set(chapterNumber, data);
  return data;
}

/**
 * Load a class-specific sidequest. Returns cached data on subsequent calls.
 * Normalizes node format to standard StoryNode.
 */
export async function loadSidequest(classId: ClassId): Promise<SidequestData> {
  const cached = sidequestCache.get(classId);
  if (cached) return cached;

  const path = sidequestPaths.get(classId);
  if (!path) {
    throw new Error(`Sidequest for class ${classId} not found`);
  }

  const loader = sidequestModules[path];
  const raw = await loader() as SidequestData;

  // Normalize nodes that may use the alternative content format
  const normalized: SidequestData = {
    ...raw,
    nodes: raw.nodes.map((node) => normalizeNode(node as unknown as RawSidequestNode)),
  };

  sidequestCache.set(classId, normalized);
  return normalized;
}

/** Get all available chapter numbers. */
export function getChapterNumbers(): number[] {
  return Array.from(chapterPaths.keys()).sort((a, b) => a - b);
}

/** Get all available sidequest class IDs. */
export function getSidequestClasses(): string[] {
  return Array.from(sidequestPaths.keys());
}

// ─── Node Index ────────────────────────────────────────────────

/** Singleton node index — built once, reused forever. */
let nodeIndex: NodeIndex | null = null;

/**
 * Build the complete node index by loading all chapters and sidequests.
 * Maps every node ID to its chapter number and node data for O(1) lookup.
 * Loads all content — call during a loading screen.
 */
export async function buildNodeIndex(): Promise<NodeIndex> {
  if (nodeIndex) return nodeIndex;

  const index: NodeIndex = new Map();

  // Load all chapters
  const chapterNumbers = getChapterNumbers();
  const chapterPromises = chapterNumbers.map((n) => loadChapter(n));
  const chapters = await Promise.all(chapterPromises);

  for (const chapter of chapters) {
    for (const node of chapter.nodes) {
      index.set(node.id, {
        chapterNumber: chapter.chapter.number,
        node,
      });
    }
  }

  // Load all sidequests
  const classIds = getSidequestPaths();
  const questPromises = classIds.map((id) => loadSidequest(id as ClassId));
  const quests = await Promise.all(questPromises);

  for (const quest of quests) {
    for (const node of quest.nodes) {
      index.set(node.id, {
        chapterNumber: node.chapter,
        node,
      });
    }
  }

  nodeIndex = index;
  return index;
}

/**
 * Get a single node by ID from the index.
 * Builds the index on first call if not already built.
 */
export async function getNode(nodeId: string): Promise<NodeIndexEntry | undefined> {
  const index = await buildNodeIndex();
  return index.get(nodeId);
}

/** Reset all caches (for testing). */
export function resetCaches(): void {
  chapterCache.clear();
  sidequestCache.clear();
  nodeIndex = null;
}

// ─── Internal helpers exposed for sidequest path lookup ────────

function getSidequestPaths(): string[] {
  return Array.from(sidequestPaths.keys());
}
